import { useId } from 'react'

interface AthenaLogoProps {
  className?: string
  size?: number
  variant?: 'default' | 'white' | 'icon'
}

export function AthenaLogo({ className = '', size = 40, variant = 'default' }: AthenaLogoProps) {
  const iconOnly = variant === 'icon'
  const isWhite = variant === 'white'

  // Stable per-instance ID for the SVG gradient. Math.random() on every
  // render meant the gradient definition broke on parent re-renders / hover
  // transitions — the eye briefly flashed without its yellow gradient,
  // which read as a small "flip" or blip in the chip animation.
  const reactId = useId()
  const id = `owl-${reactId.replace(/:/g, '')}`

  // Colors based on variant
  const bodyColor = isWhite ? '#e5e7eb' : '#1a1a2e'
  const bodyDetail = isWhite ? '#d1d5db' : '#2d2d4a'
  const faceOuter = isWhite ? '#ffffff' : '#e8dfd0'
  const faceInner = isWhite ? '#f3f4f6' : '#d4c9b8'

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Body */}
        <ellipse cx="32" cy="42" rx="18" ry="20" fill={bodyColor} />

        {/* Chest feather pattern */}
        <path
          d="M24 42 L32 50 L40 42"
          stroke={bodyDetail}
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M26 48 L32 54 L38 48"
          stroke={bodyDetail}
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />

        {/* Head */}
        <ellipse cx="32" cy="24" rx="16" ry="14" fill={bodyColor} />

        {/* Ear tufts */}
        <path d="M14 18 C12 10 16 4 20 8 C22 12 20 18 18 20" fill={bodyColor} />
        <path d="M50 18 C52 10 48 4 44 8 C42 12 44 18 46 20" fill={bodyColor} />

        {/* Inner ear tuft detail */}
        <path d="M16 14 C16 10 18 7 20 9" stroke={bodyDetail} strokeWidth="1.5" strokeLinecap="round" fill="none" />
        <path d="M48 14 C48 10 46 7 44 9" stroke={bodyDetail} strokeWidth="1.5" strokeLinecap="round" fill="none" />

        {/* Facial disc */}
        <path
          d="M32 10 C20 10 12 18 12 26 C12 32 20 38 32 38 C44 38 52 32 52 26 C52 18 44 10 32 10Z"
          fill={faceOuter}
        />
        <path
          d="M32 12 C22 12 15 18 15 25 C15 30 22 35 32 35 C42 35 49 30 49 25 C49 18 42 12 32 12Z"
          fill={faceInner}
        />

        {/* Left eye */}
        <circle cx="24" cy="24" r="8" fill={bodyColor} />
        <circle cx="24" cy="24" r="6.5" fill={`url(#${id}-eye)`} />
        <circle cx="24" cy="24" r="3.5" fill={bodyColor} />
        <circle cx="22.5" cy="22.5" r="1.5" fill="#ffffff" />

        {/* Right eye */}
        <circle cx="40" cy="24" r="8" fill={bodyColor} />
        <circle cx="40" cy="24" r="6.5" fill={`url(#${id}-eye)`} />
        <circle cx="40" cy="24" r="3.5" fill={bodyColor} />
        <circle cx="38.5" cy="22.5" r="1.5" fill="#ffffff" />

        {/* Beak */}
        <path
          d="M32 28 C30 28 28 30 28 32 C28 34 30 36 32 36 C34 36 36 34 36 32 C36 30 34 28 32 28Z"
          fill="#d97706"
        />
        <path d="M30 31 C30 30 31 29 32 29 C33 29 34 30 34 31" stroke="#92400e" strokeWidth="0.8" strokeLinecap="round" fill="none" />
        <path d="M31 34 C31 35 32 36 32 36 C32 36 33 35 33 34" stroke="#92400e" strokeWidth="1" strokeLinecap="round" fill="none" />

        {/* Feet */}
        <path d="M24 60 L22 64 M26 60 L26 64 M28 60 L30 64" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M36 60 L34 64 M38 60 L38 64 M40 60 L42 64" stroke="#64748b" strokeWidth="1.5" strokeLinecap="round" />

        {/* Wing details */}
        <path d="M14 38 C10 42 10 50 14 56" stroke={bodyDetail} strokeWidth="2" strokeLinecap="round" fill="none" />
        <path d="M50 38 C54 42 54 50 50 56" stroke={bodyDetail} strokeWidth="2" strokeLinecap="round" fill="none" />

        <defs>
          <linearGradient id={`${id}-eye`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#fcd34d" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
      </svg>

      {/* Text */}
      {!iconOnly && (
        <span className={`text-xl font-bold ${isWhite ? 'text-white' : 'text-gray-800'}`}>
          Athena
        </span>
      )}
    </div>
  )
}

export default AthenaLogo
